/**
 * views/projects/useProjectFilters.js - the Projects table's column accessors
 * (text, date, money, percent, multi-name) and the filtered, sorted rows they
 * drive. Moved verbatim out of ProjectsView.jsx; the view passes in the state
 * it reads.
 */
import { useMemo } from 'react';
import { dayKey } from '../../components/ui/DateFilterPopover';
import { computeMargin, leftoverOf, leftoverNum } from '../../utils/projectMoney';
import { fmt, fmtMoney } from '../../utils/format';
import { fmtMoney as ksaFmtMoney, fxCode } from '../../components/ksa/ksaMoney';
import { projectStatus } from '../../components/projects/ActiveClosedPill';
import { splitNames } from './tableBits';

export function useProjectFilters({ scope, sourceProjects, columnFilters, search, sort }) {
  // Map column id -> accessor function on a project row. Every visible column
  // gets one so it can be filtered through the column popover.
  // shared money formatter lives in utils/format.js
  // KSA amounts carry their record's currency code (SAR 480,000.00), never a "$"
  const money = (v, p) => (scope ? (Number(v) > 0 ? ksaFmtMoney(v, fxCode(p && p.currency) || 'SAR') : '-') : fmt(v));
  const moneyText = (v, p) => (scope ? (v != null && v !== '' ? ksaFmtMoney(v, fxCode(p && p.currency) || 'SAR') : '') : fmtMoney(v));
  const COL_ACCESSORS = {
    quoteDate: (p) => p.quoteDate || p.quote_date,
    customer: (p) => p.customer,
    subCustomer: (p) => p.subCustomer || p.sub_customer,
    description: (p) => p.description || p.name,
    approvalManager: (p) => p.approvalManager || p.approval_manager,
    engineer: (p) => p.engineer,
    quoteLetter: (p) => p.quoteLetter || p.quote_letter,
    rev: (p) => p.rev,
    quotedPrice: (p) => moneyText(p.quotedPrice ?? p.quoted_price, p),
    poDateIssued: (p) => p.poDateIssued || p.po_date_issued,
    poNumber: (p) => p.poNumber || p.po_number,
    terms: (p) => p.terms,
    poValue: (p) => moneyText(p.poValue ?? p.po_value, p),
    globalProjectNumber: (p) => p.globalProjectNumber || p.global_project_number,
    cogs: (p) => moneyText((Number(p.quotedLaborCost) || 0) + (Number(p.quotedExpenses) || 0), p),
    profit: (p) => moneyText((Number(p.quotedPrice) || 0) - ((Number(p.quotedLaborCost) || 0) + (Number(p.quotedExpenses) || 0)), p),
    margin: (p) => {
      const m = computeMargin(p);
      return m == null ? '' : `${(m * 100).toFixed(2)}%`;
    },
    latestInvoiceDate: (p) => p.latestInvoiceDate || p.latest_invoice_date,
    invoiceNumbers: (p) => p.invoiceNumbers || p.invoice_numbers,
    invoicedAlready: (p) => moneyText(p.invoicedAlready ?? p.invoiced_already, p),
    // Derived: PO - invoiced; blank when fully invoiced (see utils/projectMoney).
    moneyLeftover: (p) => {
      const l = leftoverOf(p);
      return l == null ? '' : moneyText(l, p);
    },
    // Derived Active/Closed so the column popover and chips share values
    status: (p) => projectStatus(p),
  };

  // Date columns use a different filter shape: { from, to } instead of a Set.
  const DATE_COLS = new Set(['quoteDate', 'poDateIssued', 'latestInvoiceDate']);
  // Money / percent columns use a numeric range filter { min, max }.
  const NUMERIC_COLS = new Set(['poValue', 'quotedPrice', 'cogs', 'profit', 'margin', 'invoicedAlready', 'moneyLeftover']);
  const PERCENT_COLS = new Set(['margin']);
  // Columns holding several names in one comma-separated string (r49): the
  // filter offers each name on its own and a row matches when any name is ticked.
  const MULTI_COLS = new Set(['engineer']);
  // Accessor returning the raw numeric value for range comparisons (the text
  // accessors return "$1,500.00" strings, which sorts wrong for range filters).
  const numericValueOf = (p, colId) => {
    const labor = Number(p.quotedLaborCost) || 0;
    const exp = Number(p.quotedExpenses) || 0;
    switch (colId) {
      case 'poValue':
        return Number(p.poValue ?? p.po_value) || 0;
      case 'quotedPrice':
        return Number(p.quotedPrice ?? p.quoted_price) || 0;
      case 'cogs':
        return labor + exp;
      case 'profit':
        return (Number(p.quotedPrice) || 0) - (labor + exp);
      case 'margin': {
        const price = Number(p.quotedPrice) || 0;
        if (price <= 0 || labor <= 0) return null;
        return ((price - (labor + exp)) / price) * 100;
      }
      case 'invoicedAlready':
        return Number(p.invoicedAlready ?? p.invoiced_already) || 0;
      case 'moneyLeftover':
        return leftoverNum(p);
      default:
        return null;
    }
  };

  const filtered = useMemo(() => {
    const rows = sourceProjects.filter((p) => {
      // Per-column popover filters (chips write into the same map)
      for (const [colId, value] of Object.entries(columnFilters)) {
        if (!value) continue;
        const accessor = COL_ACCESSORS[colId];
        if (!accessor) continue;
        if (DATE_COLS.has(colId)) {
          const v = accessor(p);
          if (!v) return false;
          const s = dayKey(v); // 2/14/2024 as well as 2024-02-14
          if (!s) return false;
          if (value.from && s < value.from) return false;
          if (value.to && s > value.to) return false;
          continue;
        }
        if (NUMERIC_COLS.has(colId)) {
          const n = numericValueOf(p, colId);
          if (n == null) return false;
          if (value.min != null && n < value.min) return false;
          if (value.max != null && n > value.max) return false;
          continue;
        }
        // Set-of-values text filter
        if (!value.size) continue;
        const v = accessor(p);
        if (MULTI_COLS.has(colId)) {
          if (!splitNames(v).some((n) => value.has(n))) return false;
          continue;
        }
        if (!value.has(v == null ? '' : String(v))) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        // Search every visible column (accessors) + raw fallbacks
        const hay = Object.values(COL_ACCESSORS)
          .map((a) => a(p))
          .concat([p.name, p.sub_customer])
          .filter((v) => v != null && v !== '')
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    return rows.sort((a, b) => {
      const av = a[sort.col];
      const bv = b[sort.col];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [sourceProjects, columnFilters, search, sort]); // eslint-disable-line react-hooks/exhaustive-deps
  return { filtered, COL_ACCESSORS, DATE_COLS, NUMERIC_COLS, MULTI_COLS, numericValueOf, money, PERCENT_COLS };
}
